<template>
    <slot v-bind="slotScope" />
</template>

<script>
import { CancelToken } from 'axios';
import { copy } from '@/helpers/utilities';

export default {
    name: 'Datamodel',
    inheritAttrs: false,
    props: {
        dataProp: { type: Array, default: undefined },
        url: { type: String, default: undefined },
        paging: { type: Boolean, default: false },
        params: { type: Object, default: undefined },
        autoload: { type: [Number, Boolean], default: undefined },
        pageLength: { type: Number, default: 10 },
        defaultSort: { type: Object, default: undefined },
        total: { type: Boolean, default: false },

        callback: { type: Function, default: undefined },
        asyncLength: { type: Function, default: undefined }, // e.g. (arr) => (arr.length)
        asyncPaging: { type: Function, default: undefined }, // e.g. (page, length) => ({ page, length })
        asyncSearch: { type: Function, default: undefined }, // e.g. (searchTerm) => ({ name: searchTerm })
        asyncSort: { type: Function, default: undefined },   // e.g. (field, dir) => ({ field, direction: dir })
        asyncFilter: { type: Function, default: undefined }, // e.g. (field, value) => ({ [field]: value })

        existingSelected: { type: Array, default: () => [] },
        existingData: { type: Array, default: undefined },

        skeleton: { type: Number, default: 5 },
        multisort: { type: Boolean, default: false },
        autoselect: { type: Boolean, default: false },
        multiselect: { type: Boolean, default: true },
        filterMode: { type: String, default: 'and' }, // [ and, or ]
        searchMode: { type: String, default: 'all' }, // [ all, words ]

        preventParamsWatcher: { type: Boolean, default: false },
        preventUnselect: { type: Boolean, default: false },
        preventClientSide: { type: Boolean, default: false }
    },
    emits: ['change', 'change:current', 'select', 'search', 'filter', 'sort', 'async', 'async:all-loaded', 'all-loaded'],
    data() {
        return {
            data: [],
            page: 0,
            error: null,
            isLoading: false,
            isLoadingInitial: true,
            allLoaded: false,
            allAsyncLoaded: false,

            selected: [],
            isSelecting: false,

            isAutoloading: !!this.autoload,
            autoRequests: this.autoload,
            cancelRequest: null,

            pendingFilters: {},
            activeFilters: {},
            filtersChanged: false,
            isFilteringOpen: false,
            isFilterPending: false,
            filterTimer: null,

            activeSorts: [],
            isSorting: false,

            tempData: [],
            searchTerm: '',
            staticSearch: null,
            isSearching: false,
            searchTimer: null
        };
    },
    computed: {
        dataOrTemp() {
            return this.isLoadingSubset ? this.tempData : this.data;
        },
        currentData() {
            if (!this.data) return;
            if (this.isLoadingInitial) return this.data;
            if (this.async) return this.dataOrTemp;

            let data = copy(this.dataOrTemp);
            let totalRow;

            if (this.total) totalRow = data.pop();

            if (this.activeFiltersCount) {
                const func = this.filterMode === 'and' ? 'every' : 'some';
                data = data.filter((row) => {
                    return Object.keys(this.activeFilters)[func]((filter) => {
                        if (Array.isArray(this.activeFilters[filter])) {
                            // Return true if there are no filter options
                            if (!this.activeFilters[filter].length) return true;
                            // Compare field to all filter values using `some`
                            return this.activeFilters[filter].includes(row[filter]);
                        } else {
                            // Compare field to filter value
                            return row[filter] === this.activeFilters[filter];
                        }
                    });
                });
            }

            if (this.searchTerm) {
                if (this.searchMode === 'all') {
                    data = data.filter(row => Object.keys(row).some(
                        key => {
                            if (key === 'caps') return false;

                            const value = row[key] && typeof(row[key]) === 'object'
                                ? JSON.stringify(row[key]) // Search nested objects as strings
                                : row[key];

                            return String(value).toLowerCase().includes(this.searchTerm.toLowerCase());
                        }
                    ));
                } else {
                    const words = this.searchTerm.toLowerCase().split(' ');
                    data = data.filter(row => Object.keys(row).some(
                        key => {
                            if (key === 'caps') return false;

                            const value = row[key] && typeof(row[key]) === 'object'
                                ? JSON.stringify(row[key]) // Search nested objects as strings
                                : row[key];

                            return words.every(word => String(value).toLowerCase().includes(word));
                        }
                    ));
                }
            }

            if (this.activeSorts && this.activeSorts.length) {
                this.activeSorts.forEach(sort => {
                    this.sortData(data, sort.field, sort.direction);
                });
            }

            if (this.total) data.push(totalRow);

            return data;
        },
        async() {
            return (this.paging && !this.allLoaded) || this.preventClientSide;
        },
        _params() {
            let params = this.params || {};

            if (this.async) {
                const paging = this.asyncPaging ? this.asyncPaging(this.page, this.pageLength, this.data) : { page: this.page, length: this.pageLength };
                params = { ...params, ...paging };

                if (this.searchTerm.length) {
                    const searching = this.asyncSearch ? this.asyncSearch(this.searchTerm) : { search: this.searchTerm };
                    params = { ...params, ...searching };
                }
                if (this.activeFiltersCount) {
                    Object.keys(this.activeFilters).forEach(filter => {
                        const values = this.activeFilters[filter];
                        const filtering = this.asyncFilter ? this.asyncFilter(filter, values, params) : { [filter]: values };
                        params = { ...params, ...filtering };
                    });
                }
                if (this.activeSorts && this.activeSorts.length) {
                    const sort = this.activeSorts[0];
                    const sorting = this.asyncSort ? this.asyncSort(sort.field, sort.direction) : { sort: JSON.stringify({ predicate: sort.field, reverse: (sort.direction === 'desc') }) };
                    params = { ...params, ...sorting };
                }
            }

            return params;
        },
        activeFiltersCount() {
            return Object.values(this.activeFilters).filter(filter => Array.isArray(filter) ? filter.length > 0 : filter).length;
        },
        pendingFiltersCount() {
            return Object.values(this.pendingFilters).filter(filter => Array.isArray(filter) ? filter.length > 0 : filter).length;
        },
        isLoadingSubset() {
            return this.async && (this.isSearching || this.isFiltering);
        },
        isLoadingMore() {
            return this.isLoading && !this.isLoadingInitial && !this.isAutoloading && !this.isLoadingSubset;
        },
        isFiltering() {
            const paramsSet = this.params && Object.values(this.params).filter(Boolean) || [];
            return this.activeFiltersCount || paramsSet.length;
        },
        selectable() {
            if (!(this.currentData && this.currentData.length)) return [];
            return this.currentData.filter(row => !this.preventSelection(row));
        },
        slotScope() {
            return {
                data: this.data,
                async: this.async,
                getData: this.getData,
                currentData: this.currentData,
                countBy: this.countBy,
                updateRow: this.updateRow,
                error: this.error,

                // Selecting
                selected: this.selected,
                selectable: this.selectable,
                select: this.select,
                selectAll: this.selectAll,
                clearSelection: this.clearSelection,
                preventSelection: this.preventSelection,
                isSelected: this.isSelected,
                isSelecting: this.isSelecting,

                // Sorting
                setSort: this.setSort,
                forceSort: this.forceSort,
                activeSorts: this.activeSorts,
                getSortDirection: this.getSortDirection,
                isSorting: this.isSorting,

                // Filters
                activeFilters: this.activeFilters,
                activeFiltersCount: this.activeFiltersCount,
                pendingFiltersCount: this.pendingFiltersCount,
                setFilter: this.setFilter,
                openFilter: this.openFilter,
                closeFilter: this.closeFilter,
                applyFilter: this.applyFilter,
                clearFilter: this.clearFilter,
                clearFilterValue: this.clearFilterValue,
                countActiveFilters: this.countActiveFilters,

                isFiltering: this.isFiltering,
                isFilteringOpen: this.isFilteringOpen,
                isFilterPending: this.isFilterPending,
                isFilterSelected: this.isFilterSelected,
                isFilterActive: this.isFilterActive,

                // Search
                searchTerm: this.searchTerm,
                setSearch: this.setSearch,
                isSearching: this.isSearching,

                // State
                allLoaded: this.allLoaded,
                isLoading: this.isLoading,
                isLoadingInitial: this.isLoadingInitial,
                isAutoloading: this.isAutoloading,
                isLoadingMore: this.isLoadingMore,
                isLoadingSubset: this.isLoadingSubset,
                canCopy: this.canCopy,
                canDelete: this.canDelete
            };
        }
    },
    watch: {
        url(to, from) {
            if (to === from) return;
            if (!this.isLoadingInitial && to && from) this.refreshData();
        },
        data: {
            handler() {
                if (!this.isLoadingInitial) this.$emit('change', this.data);
            },
            deep: true
        },
        currentData: {
            handler() {
                this.$emit('change:current', this.currentData);
            },
            deep: true
        },
        async(to) {
            this.$emit('async', to);
        },
        allLoaded(to) {
            this.$emit('all-loaded', to);
        },
        allAsyncLoaded(to) {
            this.$emit('async:all-loaded', to, this.tempData);
        },
        dataProp: {
            handler(to) {
                if (to) {
                    this.data = this.callback ? this.callback(to, this.data, this.isLoadingInitial) : to;
                    this.isLoadingInitial = false;
                    this.isLoading = false;
                    this.allLoaded = true;
                    this.stopAutoloading();

                    if(this.autoselect) this.select(this.data[0]);
                    if (this.existingSelected && this.existingSelected.length) this.selectInitial();
                }
            },
            deep: true,
            immediate: true
        },
        selected: {
            handler(to) {
                this.$emit('select', to);
            },
            deep: true
        },
        params: {
            handler() {
                if (this.async && !this.preventParamsWatcher) this.resetAsync();
            },
            deep: true
        },
        searchTerm(to, from) {
            if (to === from) return;
            this.stopAutoloading();

            if (this.async) {
                this.searchTimer = this.$debounce(() => {
                    this.$emit('search', to);
                    this.isSearching = !!(to && to.length);
                    this.resetAsync();
                }, 600, this.searchTimer);
            } else {
                this.searchTimer = this.$debounce(() => {
                    this.$emit('search', to);
                }, 500, this.searchTimer);
            }
        },
        activeFilters: {
            handler() {
                this.$emit('filter');
                this.stopAutoloading();
                if (this.async) this.resetAsync();
            },
            deep: true
        },
        activeSorts: {
            handler() {
                this.$emit('sort');
                this.stopAutoloading();

                if (this.async) {
                    this.isSorting = !!(this.activeSorts && this.activeSorts.length);
                    this.resetAsync();
                }
            },
            deep: true
        },
        filterMode() {
            this.clearAllFilters();
        }
    },
    mounted() {
        if (this.autoload !== false) {
            if (this.skeleton && !this.data.length) this.data = this.getSkeletonData();
            if (this.url) {
                if (this.defaultSort) this.forceSort(this.defaultSort);
                this.getData();
            }
        } else {
            this.isLoadingInitial = false;
        }

        if (this.$bus) {
            this.$bus.on(`${this.url}:refresh`, this.refreshData);
            this.$bus.on(`${this.url}:create`, this.addRow);
            this.$bus.on(`${this.url}:upsert`, this.upsertRow);
            this.$bus.on(`${this.url}:update`, this.updateRow);
            this.$bus.on(`${this.url}:push`, this.addToRow);
            this.$bus.on(`${this.url}:delete`, this.deleteRow);
        }
    },
    unmounted() {
        if (this.$bus) {
            this.$bus.off(`${this.url}:refresh`);
            this.$bus.off(`${this.url}:create`);
            this.$bus.off(`${this.url}:upsert`);
            this.$bus.off(`${this.url}:update`);
            this.$bus.off(`${this.url}:push`);
            this.$bus.off(`${this.url}:delete`);
        }
    },
    methods: {
        getSkeletonData() {
            return Array.from({ length: this.skeleton }, Object);
        },
        async getData() {
            if (this.isLoading || this.allLoaded || this.allAsyncLoaded || !this.url) return;
            this.isLoading = true;
            this.error = null;
            return this.$http.get(this.url, { params: this._params, cancelToken: new CancelToken(cancel => this.cancelRequest = cancel) })
                .then(response => {
                    let data = response.data;
                    if (!data) data = [];
                    const existing = this.isLoadingSubset ? this.tempData : this.data;
                    const results = this.callback ? this.callback(data, existing, this.isLoadingInitial) : data;

                    this.isLoading = false;
                    this.isLoadingInitial = false;
                    this.cancelRequest = null;

                    if (this.isLoadingSubset) {
                        this.tempData.push(...results);
                    } else if (this.page === 0) {
                        this.data = results;
                        if (this.existingSelected && this.existingSelected.length) this.selectInitial();
                    } else {
                        this.data.push(...results);
                    }

                    if(this.autoselect) this.select(this.data[0]);

                    if (this.async) {
                        const currentLength = this.asyncLength ? this.asyncLength(data) : results.length;

                        if (currentLength < this.pageLength) {
                            this.stopAutoloading();

                            if (this.isLoadingSubset) {
                                this.allAsyncLoaded = true;
                            } else {
                                this.allLoaded = true;

                            }
                        } else {
                            this.page++;

                            if (this.isAutoloading) {
                                this.autoRequests--;
                                if (this.autoRequests) {
                                    return this.getData();
                                } else {
                                    this.isAutoloading = false;
                                }
                            }
                        }
                    } else {
                        this.allLoaded = true;
                    }
                }).catch(error => {
                    if (this.$http.isCancel && this.$http.isCancel(error)) return;
                    if (this.isLoadingInitial) this.data = [];
                    this.error = {
                        code: error.response && error.response.status,
                        message: error.response && error.response.statusText
                    };
                    this.isLoading = false;
                    this.isLoadingInitial = false;
                    this.cancelRequest = null;
                });
        },
        resetAsync() {
            if (this.isLoadingInitial) return;
            this.tempData = [];
            this.allAsyncLoaded = false;
            this.refreshData();
        },
        cancelCurrentRequest() {
            this.isLoading = false;
            if (this.cancelRequest) this.cancelRequest();
        },
        stopAutoloading() {
            if (!this.isAutoloading) return;

            this.autoRequests = 0;
            this.isAutoloading = false;
            this.cancelCurrentRequest();
        },
        getRowIndex(row, criteria = {}) {
            if (!(this.dataOrTemp && this.dataOrTemp.length)) return -1;
            const index = this.dataOrTemp.indexOf(row);
            return (~index ? index : this.dataOrTemp.findIndex(x => row && x.id === row.id && Object.keys(criteria).every(key => x[key] === criteria[key])));
        },
        refreshData() {
            this.page = 0;
            this.allLoaded = false;
            this.isLoadingInitial = true;
            this.data = this.skeleton ? this.getSkeletonData() : [];

            this.cancelCurrentRequest();
            this.getData();
        },
        addRow(row) {
            this.dataOrTemp.unshift(row);
        },
        updateRow(row) {
            const index = this.getRowIndex(row);
            if (~index) this.dataOrTemp[index] = { ...this.dataOrTemp[index], ...row };
        },
        upsertRow(row) {
            if (~this.getRowIndex(row)) {
                this.updateRow(row);
            } else {
                this.addRow(row);
            }
        },
        addToRow(row) {
            const index = this.getRowIndex(row);

            const updated = Object.assign({}, this.dataOrTemp[index]);

            Object.keys(row).forEach(key => {
                if (key === 'id') return;

                const value = row[key];

                if (typeof value === 'number') {
                    return updated[key] = this.dataOrTemp[index][key] + value;
                } else if (Array.isArray(value)) {
                    return updated[key].push(...value);
                }
            });

            this.dataOrTemp[index] = updated;
        },
        deleteRow(row) {
            const index = this.getRowIndex(row);
            if (~index) this.dataOrTemp.splice(index, 1);
        },
        canDelete(caps = this.$access.caps) {
            return caps.delete;
        },
        canCopy(caps = this.$access.caps) {
            if (this.url === '/api/v1/groups') return caps.manage_enrolment;
            return caps.copy;
        },
        countBy(field, value) {
            return this.data.filter(row => row[field] === value).length;
        },
        isSelected(row) {
            return this.selected.some(selected => selected.id === row.id || selected === row);
        },
        clearSelection() {
            this.isSelecting = false;
            if (this.selected.length) this.selected = [];
        },
        preventSelection(row) {
            return row.$disabled || (this.existingData && this.existingData.some(e => e.id && row.id && Number(e.id) === Number(row.id)));
        },
        selectInitial() {
            this.existingSelected.forEach(row => {
                const exists = this.getRowIndex(row);
                if (~exists) this.select(this.data[exists]);
            });
        },
        selectAll(select = true) {
            if (this.selectable.length === 0 || !select) return this.clearSelection();
            this.isSelecting = true;
            this.$nextTick(() => this.selected = this.selectable);
        },
        select(row, clear = false) {
            if (this.isLoadingInitial) return;
            if (this.preventSelection(row)) return;
            if (this.preventUnselect && this.isSelected(row)) return;

            const index = this.getRowIndex(row);
            if (~index) row = this.dataOrTemp[index];
            this.isSelecting = true;
            if (this.isSelected(row) || clear) {
                const index = this.selected.findIndex(selected => selected.id === row.id || selected === row);
                if (~index) this.selected.splice(index, 1);
            } else {
                this.multiselect ? this.selected.push(row) : this.selected = [row];
            }
            if (this.selected.length === 0) this.isSelecting = false;
        },
        setSearch(value) {
            this.searchTerm = value;
        },
        forceSort(sort, cb) {
            const index = this.activeSorts.indexOf(sort);

            if (this.multisort) {
                if(~index) {
                    this.activeSorts.splice(index, 1);
                } else {
                    this.activeSorts.push(sort);
                    if (cb) cb();
                }
            } else {
                if (~index) {
                    this.activeSorts = [];
                } else {
                    this.activeSorts = [sort];
                    if (cb) cb();
                }
            }
        },
        setSort(column) {
            if (!(column.sortable && column.sortable(this.async))) return;
            this.multisort ? this.doMultiSort(column) : this.doSingleSort(column);
        },
        doMultiSort(column) {
            const index = this.getSortPosition(column);

            if (~index) {
                if (this.activeSorts[index].direction === 'asc') {
                    this.activeSorts[index].direction = 'desc';
                } else {
                    this.activeSorts.splice(index, 1);
                }
            } else {
                this.activeSorts.unshift({ field: column.field, direction: 'asc' });
            }
        },
        doSingleSort(column) {
            if (this.getSortPosition(column) === 0) {
                this.activeSorts[0].direction = column.direction || (this.activeSorts[0].direction === 'asc' ? 'desc' : 'asc');
            } else {
                this.activeSorts = [{ field: column.field, direction: column.direction || 'asc' }];
            }
        },
        getSortDirection(column) {
            const index = this.getSortPosition(column);
            if (~index && this.activeSorts[index]) return this.activeSorts[index].direction;
        },
        getSortPosition(column) {
            if (!(this.activeSorts && this.activeSorts.length)) return -1;
            return this.activeSorts.findIndex(sort => sort.field === column.field);
        },
        sortData(data, field, direction) {
            data.sort((a, b) => {
                const compareA = direction === 'asc' ? a[field] : b[field];
                const compareB = direction === 'asc' ? b[field] : a[field];

                if (this.$date(compareA).isValid() || this.$date(compareB).isValid()) {
                    return !this.$date(compareB).isValid() || this.$date(compareA).isAfter(this.$date(compareB)) ? 1 : -1;
                } else if (typeof compareA === 'string' && typeof compareB === 'string') {
                    return compareA.localeCompare(compareB, window.navigator.language, { numeric: true });
                } else {
                    return compareA > compareB ? 1 : -1;
                }
            });
        },
        forceFilter(filter, value) {
            if (filter.multiselect && filter.multiselect(this.async)) {
                if (!(this.activeFilters && this.activeFilters[filter.by])) this.activeFilters[filter.by] = [];
                this.activeFilters[filter.by].push(value);
            } else {
                this.activeFilters[filter.by] = value;
            }
        },
        openFilter() {
            this.pendingFilters = Object.assign({}, this.activeFilters);
            this.isFilteringOpen = true;
        },
        closeFilter() {
            this.isFilteringOpen = false;
            this.isFilterPending = false;
        },
        applyFilter() {
            this.activeFilters = Object.assign({}, this.pendingFilters);
            this.closeFilter();
        },
        setFilter(filter, value, pending = false) {
            const target = pending ? this.pendingFilters : this.activeFilters;

            if (pending) this.isFilterPending = true;

            if (filter.multiselect && filter.multiselect(this.async)) {
                if (value === 'all') return target[filter.by] = [];

                if (!target[filter.by]) {
                    // Setup an empty array to hold filters if none
                    target[filter.by] = [];
                }

                // Toggle option off or on depending on current state
                if (target[filter.by].includes(value)) {
                    return this.clearFilterValue(filter, value, pending);
                } else {
                    target[filter.by].push(value);
                }
            } else {
                if (this.isFilterSelected(filter, value, pending)) {
                    return this.clearFilterValue(filter, value, pending);
                } else {
                    target[filter.by] = value;
                }
            }
        },
        isFilterActive(filter, pending = false) {
            const target = pending ? this.pendingFilters : this.activeFilters;

            if (filter.multiselect && filter.multiselect(this.async)) {
                return !!(target[filter.by] && target[filter.by].length);
            } else {
                return !!target[filter.by];
            }
        },
        isFilterSelected(filter, value, pending = false) {
            const target = pending ? this.pendingFilters : this.activeFilters;

            if (filter.multiselect && filter.multiselect(this.async)) {
                return !!(target[filter.by] && target[filter.by].includes(value));
            } else {
                return !!(target[filter.by] && target[filter.by] === value);
            }
        },
        countActiveFilters(filter) {
            if (filter.multiselect && filter.multiselect(this.async)) {
                return this.activeFilters[filter.by] && this.activeFilters[filter.by].length;
            } else {
                return this.activeFilters[filter.by] ? 1 : 0;
            }
        },
        clearFilter(filter, pending = false) {
            const target = pending ? this.pendingFilters : this.activeFilters;

            if (target[filter.by]) {
                if (pending) this.isFilterPending = true;
                delete target[filter.by];
            }
        },
        clearFilterValue(filter, value, pending = false) {
            const target = pending ? this.pendingFilters : this.activeFilters;

            if (filter.multiselect && filter.multiselect(this.async) && value) {
                target[filter.by].splice(target[filter.by].indexOf(value), 1);
                if (target[filter.by].length === 0) delete target[filter.by];
            } else {
                delete target[filter.by];
            }
        },
        clearAllFilters() {
            this.activeFilters = {};
        },
    }
};
</script>
